import { useState, useContext, useEffect } from 'react';
import MediaLibrary from './MediaLibrary'; 
import BulkDiscount from './BulkDiscount'; 
import ImageUpload from './ImageUpload'; 
import CategoryManager from './CategoryManager';
import CategoryExtraImagesManager from './CategoryExtraImagesManager';
import EnhancedProductForm from './EnhancedProductForm';
import BulkImport from './BulkImport';
import BulkProductCreator from './BulkProductCreator';
import Orders from './Orders';
import EnhancedMediaLibrary from './EnhancedMediaLibrary';
import BulkVariantManager from './BulkVariantManager';
import ChatStats from './ChatStats';
import CustomOrders from './CustomOrders';
import PriceManager from './PriceManager';
import DescriptionManager from './DescriptionManager';
import StockManager from './StockManager';
import AdminSettings from './AdminSettings';
import CustomizableProductManager from './CustomizableProductManager';
import EmailManager from './EmailManager';
import ProductTagManager from './ProductTagManager';
import Loader from '../shared/Loader';
import { ProductContext } from '../../providers/ProductContext'; 
import { CategoryContext } from '../../providers/CategoryContext';
import { AuthContext } from '../../providers/AuthContext';
import { showError, showSuccess, showDeleteConfirm } from '../../utils/sweetAlert';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { useDebounce } from '../../hooks/useDebounce';
import { ProductTagContext } from '../../providers/ProductTagContext';
import { normalizeProductTag } from '../../config/productTags';
import '../../styles/Dashboard.css';


const Dashboard = () => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [discount, setDiscount] = useState(0);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  // Estado propio del Dashboard: paginado/filtrado por el admin, independiente
  // del catálogo global de ProductContext (que la tienda usa completo).
  const [products, setProducts] = useState([]);
  const { refetchProducts } = useContext(ProductContext);
  const { categories, getCategoriesLegacyFormat } = useContext(CategoryContext);
  const { tagGroups } = useContext(ProductTagContext);
  const { token } = useContext(AuthContext);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('products');
  const [viewMode, setViewMode] = useState('grid');
  const [showEnhancedForm, setShowEnhancedForm] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ category: '', discount: '', isActive: '', tagsToAdd: [], tagsToRemove: [] });
  const [isApplyingBulkEdit, setIsApplyingBulkEdit] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, hasMore: false });
  const [isLoading, setIsLoading] = useState(false);
  const [stockDrafts, setStockDrafts] = useState({});
  const [savingStockId, setSavingStockId] = useState(null);
  const productsPerPage = 20;

  // Obtener categorías en formato legacy para compatibilidad
  const legacyCategories = getCategoriesLegacyFormat();

  // Función para cargar productos con paginación
  const fetchProducts = async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: productsPerPage.toString(),
        includeInactive: 'true'
      });
      
      if (debouncedSearchQuery) {
        params.append('search', debouncedSearchQuery);
      }
      if (filterCategory) {
        params.append('category', filterCategory);
      }
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products?${params}`);
      const data = await response.json();
      
      if (data.products) {
        setProducts(data.products);
        setPagination(data.pagination);
      } else {
        // Compatibilidad con respuesta antigua (sin paginación)
        setProducts(data);
        setPagination({ total: data.length, totalPages: 1, hasMore: false });
      }
    } catch (error) {
      showError('Error al cargar productos');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar productos cuando cambie la página o el tab
  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts(currentPage);
    }
  }, [currentPage, activeTab]);

  // Resetear a página 1 y recargar cuando cambie búsqueda o filtro
  useEffect(() => {
    if (activeTab === 'products') {
      setCurrentPage(1);
      // Forzar recarga con página 1
      fetchProducts(1);
    }
  }, [debouncedSearchQuery, filterCategory, filterStatus]);

  const getProductStockValue = (product) => {
    const numericStock = Number(product?.stock);
    return Number.isFinite(numericStock) ? numericStock : null;
  };

  const getStockInputValue = (product) => {
    const draftValue = stockDrafts[product._id];
    if (draftValue !== undefined) {
      return draftValue;
    }

    return product.stock ?? '';
  };

  const handleStockDraftChange = (productId, value) => {
    setStockDrafts(prev => ({
      ...prev,
      [productId]: value
    }));
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setSubcategory('');
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowEnhancedForm(true);
  };

  const handleNewProduct = () => {
    setEditingProduct(null);
    setShowEnhancedForm(true);
  };

  const handleFormCancel = () => {
    setShowEnhancedForm(false);
    setEditingProduct(null);
  };

  const handleFormSave = async () => {
    await fetchProducts(currentPage);
    setShowEnhancedForm(false);
    setEditingProduct(null);
  };

  const handleSaveStock = async (productId) => {
    const rawStock = stockDrafts[productId] ?? products.find(product => product._id === productId)?.stock;

    if (rawStock === '' || rawStock === null || rawStock === undefined) {
      showError('Ingresa un stock válido');
      return;
    }

    const parsedStock = Number(rawStock);
    if (!Number.isInteger(parsedStock) || parsedStock < 0) {
      showError('El stock debe ser un número entero mayor o igual a 0');
      return;
    }

    setSavingStockId(productId);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${productId}`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ stock: parsedStock })
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.error || 'No se pudo actualizar el stock');
      }

      setProducts(prev =>
        prev.map(product =>
          product._id === productId
            ? { ...product, stock: responseData.stock ?? parsedStock }
            : product
        )
      );

      setStockDrafts(prev => {
        const nextDrafts = { ...prev };
        delete nextDrafts[productId];
        return nextDrafts;
      });

      showSuccess('Stock actualizado con éxito');
      refetchProducts();
    } catch (error) {
      console.error('Error al actualizar stock:', error);
      showError(error.message || 'Hubo un problema al actualizar el stock');
    } finally {
      setSavingStockId(null);
    }
  };

  // Funciones para selección masiva
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p._id));
    }
  };

  // Eliminación masiva
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      showError('Selecciona al menos un producto para eliminar');
      return;
    }

    const result = await showDeleteConfirm(`¿Estás seguro de que quieres eliminar ${selectedProducts.length} productos?`);
    if (!result.isConfirmed) return;

    try {
      const deletePromises = selectedProducts.map(id => 
        fetch(`${process.env.REACT_APP_API_URL}/products/${id}`, {
          method: 'DELETE',
          headers: getAdminAuthHeaders(token)
        })
      );

      await Promise.all(deletePromises);
      
      showSuccess(`${selectedProducts.length} productos eliminados con éxito`);
      
      // Actualizar lista de productos (vista admin) y catálogo global (tienda)
      await fetchProducts(currentPage);
      refetchProducts();
      setSelectedProducts([]);
    } catch (error) {
      console.error('Error al eliminar productos:', error);
      showError('Hubo un problema al eliminar los productos');
    }
  };

  // Edición masiva
  const handleBulkEdit = async () => {
    if (selectedProducts.length === 0) {
      showError('Selecciona al menos un producto para editar');
      return;
    }

    const updates = {};
    if (bulkEditData.category) updates.category = bulkEditData.category;
    if (bulkEditData.discount !== '') updates.discount = Number(bulkEditData.discount);
    if (bulkEditData.isActive !== '') updates.isActive = bulkEditData.isActive === 'true';
    const hasTagUpdates = bulkEditData.tagsToAdd.length > 0 || bulkEditData.tagsToRemove.length > 0;

    if (Object.keys(updates).length === 0 && !hasTagUpdates) {
      showError('Selecciona al menos un campo para actualizar');
      return;
    }

    setIsApplyingBulkEdit(true);
    try {
      const requests = [];

      if (Object.keys(updates).length > 0) {
        requests.push(Promise.all(selectedProducts.map(id =>
          fetch(`${process.env.REACT_APP_API_URL}/products/${id}`, {
            method: 'PUT',
            headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
            body: JSON.stringify(updates)
          }).then(async response => {
            if (!response.ok) {
              const data = await response.json().catch(() => ({}));
              throw new Error(data.error || 'No se pudo actualizar un producto');
            }
          })
        )));
      }

      if (hasTagUpdates) {
        requests.push(fetch(`${process.env.REACT_APP_API_URL}/products/bulk-tags`, {
          method: 'PUT',
          headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            productIds: selectedProducts,
            addTags: bulkEditData.tagsToAdd,
            removeTags: bulkEditData.tagsToRemove
          })
        }).then(async response => {
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || 'No se pudieron actualizar las etiquetas');
          }
        }));
      }

      await Promise.all(requests);
      
      showSuccess(`${selectedProducts.length} productos actualizados con éxito`);
      
      // Actualizar lista de productos (vista admin) y catálogo global (tienda)
      await fetchProducts(currentPage);
      refetchProducts();
      setSelectedProducts([]);
      setShowBulkActions(false);
      setBulkEditData({ category: '', discount: '', isActive: '', tagsToAdd: [], tagsToRemove: [] });
    } catch (error) {
      console.error('Error al actualizar productos:', error);
      showError(error.message || 'Hubo un problema al actualizar los productos');
    } finally {
      setIsApplyingBulkEdit(false);
    }
  };

  const toggleBulkTag = (tag, action) => {
    const tagIsEqual = (item) => normalizeProductTag(item) === normalizeProductTag(tag);
    setBulkEditData(prev => {
      const key = action === 'add' ? 'tagsToAdd' : 'tagsToRemove';
      const oppositeKey = action === 'add' ? 'tagsToRemove' : 'tagsToAdd';
      const isSelected = prev[key].some(tagIsEqual);
      return {
        ...prev,
        [key]: isSelected ? prev[key].filter(item => !tagIsEqual(item)) : [...prev[key], tag],
        [oppositeKey]: prev[oppositeKey].filter(item => !tagIsEqual(item))
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let sizes = [];
    let colors = [];

    if (legacyCategories[category] && legacyCategories[category].details && subcategory) {
      // Talles y colores de la subcategoría
      sizes = legacyCategories[category].details[subcategory]?.sizes || [];
      colors = legacyCategories[category].details[subcategory]?.colors || [];
    } else if (legacyCategories[category]) {
      // Talles y colores de la categoría
      sizes = legacyCategories[category].sizes || [];
      colors = legacyCategories[category].colors || [];
    } else {
      // Categoría desconocida: arrays vacíos
      sizes = [];
      colors = [];
    }    
    
    
    const updatedProduct = {
      name,
      price: Number(price),
      discount: Number(discount),
      description,
      category,
      subcategory: subcategory || 'N/A',
      size: sizes, 
      color: colors,  
      image: selectedImage ? selectedImage.filename : (editingProduct ? editingProduct.image : null)
    };
      
    try {
      if (editingProduct) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${editingProduct._id}`, {
          method: 'PUT',
          headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify(updatedProduct)
        });

        if (!response.ok) {
          throw new Error('Error al actualizar el producto');
        }
        showSuccess('Producto actualizado con éxito');
      } else {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/products`, {
          method: 'POST',
          headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify(updatedProduct)
        });

        if (!response.ok) {
          throw new Error('Error al agregar el producto');
        }
        showSuccess('Producto agregado con éxito');
      }

      await fetchProducts(currentPage);
      refetchProducts();

      // Limpiar formulario
      setEditingProduct(null);
      setName('');
      setPrice('');
      setDiscount(0);
      setDescription('');
      setCategory('');
      setSubcategory('');
      setSelectedImage(null);
    } catch (error) {
      console.error(error);
      showError('Ocurrió un error al guardar el producto');
    }
  };

  const handleDuplicate = async (product) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${product._id}/duplicate`, {
        method: 'POST',
        headers: getAdminAuthHeaders(token)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al duplicar el producto');
      }

      showSuccess(`"${product.name}" duplicado con éxito`);
      await fetchProducts(currentPage);
      refetchProducts();
    } catch (error) {
      console.error('Error al duplicar producto:', error);
      showError(error.message || 'Hubo un problema al duplicar el producto');
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      showError('Error: Producto sin ID válido');
      return;
    }
    
    const result = await showDeleteConfirm("¿Estás seguro de que quieres eliminar este producto?");
    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${id}`, {
        method: 'DELETE',
        headers: getAdminAuthHeaders(token)
      });

      if (response.ok) {
        showSuccess('Producto eliminado con éxito');
        await fetchProducts(currentPage);
        refetchProducts();
      } else {
        const errorData = await response.json();
        showError(`Error al eliminar el producto: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error al eliminar el producto:', error);
      showError('Hubo un problema al eliminar el producto');
    }
  };

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Listen for toggle event dispatched by client Menu
  useEffect(() => {
    const handleToggle = () => setSidebarOpen(prev => !prev);
    window.addEventListener('toggleDashboardSidebar', handleToggle);
    return () => window.removeEventListener('toggleDashboardSidebar', handleToggle);
  }, []);

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  // Los productos ya vienen filtrados del backend
  const filteredProducts = products;

  return (
    <>
      <div className="dashboard-container">
        {/* Overlay (mobile) */}
        {sidebarOpen && (
          <div
            className="dashboard-sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`dashboard-sidebar${sidebarOpen ? ' open' : ''}`}>
          <div className="dashboard-sidebar-header">
            <h2>Dashboard</h2>
            <p>Panel de Control</p>
          </div>
          <nav className="dashboard-sidebar-menu">
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => handleNavClick('products')}
            >
              <i className="fas fa-box"></i>
              <span>Productos</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'categories' ? 'active' : ''}`}
              onClick={() => handleNavClick('categories')}
            >
              <i className="fas fa-tags"></i>
              <span>Categorías</span>
            </div>
            <div
              className={`dashboard-sidebar-item ${activeTab === 'product-tags' ? 'active' : ''}`}
              onClick={() => handleNavClick('product-tags')}
            >
              <i className="fas fa-tags"></i>
              <span>Etiquetas</span>
            </div>
            <div
              className={`dashboard-sidebar-item ${activeTab === 'category-images' ? 'active' : ''}`}
              onClick={() => handleNavClick('category-images')}
            >
              <i className="fas fa-layer-group"></i>
              <span>Imágenes por Categoría</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => handleNavClick('orders')}
            >
              <i className="fas fa-shopping-cart"></i>
              <span>Órdenes</span>
            </div>
            <div
              className={`dashboard-sidebar-item ${activeTab === 'emails' ? 'active' : ''}`}
              onClick={() => handleNavClick('emails')}
            >
              <i className="fas fa-envelope"></i>
              <span>Emails</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'media' ? 'active' : ''}`}
              onClick={() => handleNavClick('media')}
            >
              <i className="fas fa-images"></i>
              <span>Biblioteca de Medios</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'variants' ? 'active' : ''}`}
              onClick={() => handleNavClick('variants')}
            >
              <i className="fas fa-th"></i>
              <span>Gestor de Variantes</span>
            </div>
            <div
              className={`dashboard-sidebar-item ${activeTab === 'bulk-create' ? 'active' : ''}`}
              onClick={() => handleNavClick('bulk-create')}
            >
              <i className="fas fa-wand-magic-sparkles"></i>
              <span>Carga Visual</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'bulk-import' ? 'active' : ''}`}
              onClick={() => handleNavClick('bulk-import')}
            >
              <i className="fas fa-file-upload"></i>
              <span>Importación JSON</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'bulk-discount' ? 'active' : ''}`}
              onClick={() => handleNavClick('bulk-discount')}
            >
              <i className="fas fa-percentage"></i>
              <span>Descuento Masivo</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'chat-stats' ? 'active' : ''}`}
              onClick={() => handleNavClick('chat-stats')}
            >
              <i className="fas fa-comments"></i>
              <span>Chat Inteligente</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'custom-orders' ? 'active' : ''}`}
              onClick={() => handleNavClick('custom-orders')}
            >
              <i className="fas fa-pen-ruler"></i>
              <span>Pedidos Merchandising</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'stock-manager' ? 'active' : ''}`}
              onClick={() => handleNavClick('stock-manager')}
            >
              <i className="fas fa-boxes-stacked"></i>
              <span>Gestor de Stock</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'price-manager' ? 'active' : ''}`}
              onClick={() => handleNavClick('price-manager')}
            >
              <i className="fas fa-dollar-sign"></i>
              <span>Gestor de Precios</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'description-manager' ? 'active' : ''}`}
              onClick={() => handleNavClick('description-manager')}
            >
              <i className="fas fa-file-alt"></i>
              <span>Descripciones</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'customizable-products' ? 'active' : ''}`}
              onClick={() => handleNavClick('customizable-products')}
            >
              <i className="fas fa-wand-magic-sparkles"></i>
              <span>Personalizado IA</span>
            </div>
            <div 
              className={`dashboard-sidebar-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => handleNavClick('settings')}
            >
              <i className="fas fa-gear"></i>
              <span>Configuraciones</span>
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">

        {/* Contenido de las pestañas */}
        {activeTab === 'products' && (
          <div>
            {/* Header */}
            <div className="dashboard-header">
              <h1>Productos</h1>
              <p>Gestiona tu catálogo de productos</p>
            </div>

            {/* Formulario mejorado */}
            {showEnhancedForm && (
              <div className="mb-4">
                <EnhancedProductForm
                  editingProduct={editingProduct}
                  onSave={handleFormSave}
                  onCancel={handleFormCancel}
                />
              </div>
            )}

            {/* Toolbar con búsqueda y acciones */}
            {!showEnhancedForm && (
              <>
                <div className="dashboard-toolbar">
                  <div className="toolbar-row">
                    <div className="toolbar-search">
                      <input
                        type="text"
                        placeholder="🔍 Buscar productos..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && searchQuery !== debouncedSearchQuery && (
                        <small className="text-muted ms-2">Buscando...</small>
                      )}
                    </div>
                    <div className="toolbar-filter">
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                      >
                        <option value="">Todas las categorías</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat.name}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="toolbar-filter">
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">Todos los estados</option>
                        <option value="active">Solo activos</option>
                        <option value="inactive">Solo inactivos</option>
                      </select>
                    </div>
                    {(searchQuery || filterCategory || filterStatus !== 'all') && (
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setSearchQuery('');
                          setFilterCategory('');
                          setFilterStatus('all');
                        }}
                        title="Limpiar filtros"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    <div className="view-mode-toggle">
                      <button
                        className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        title="Vista de grilla"
                      >
                        <i className="fas fa-th"></i>
                      </button>
                      <button
                        className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        title="Vista de lista"
                      >
                        <i className="fas fa-list"></i>
                      </button>
                    </div>
                    <button 
                      className="btn-primary-custom"
                      onClick={handleNewProduct}
                    >
                      <i className="fas fa-plus"></i>
                      Nuevo Producto
                    </button>
                  </div>
                </div>
                
                {/* Contador de resultados */}
                {!isLoading && pagination.total > 0 && (
                  <div className="mb-3 text-muted small">
                    Mostrando {pagination.total} producto{pagination.total !== 1 ? 's' : ''}
                    {(debouncedSearchQuery || filterCategory || filterStatus !== 'all') && ' (filtrados)'}
                  </div>
                )}
              </>
            )}
              
              {/* Acciones masivas */}
              {selectedProducts.length > 0 && (
                <div className="prodmgr-bulk-bar">
                  <div className="prodmgr-bulk-header">
                    <span><strong>{selectedProducts.length}</strong> productos seleccionados</span>
                    <div className="prodmgr-bulk-btns">
                      <button className="prodmgr-bulk-edit-btn" onClick={() => setShowBulkActions(!showBulkActions)}>
                        <i className="fas fa-edit"></i> Editar
                      </button>
                      <button className="prodmgr-bulk-delete-btn" onClick={handleBulkDelete}>
                        <i className="fas fa-trash"></i> Eliminar
                      </button>
                    </div>
                  </div>
                  
                  {showBulkActions && (
                    <div className="prodmgr-bulk-form">
                      <div className="prodmgr-bulk-form-grid">
                        <div className="prodmgr-bulk-field">
                          <label>Categoría</label>
                          <select value={bulkEditData.category}
                            onChange={(e) => setBulkEditData(prev => ({...prev, category: e.target.value}))}>
                            <option value="">No cambiar</option>
                            {categories.map((cat) => (
                              <option key={cat._id} value={cat.name}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="prodmgr-bulk-field">
                          <label>Descuento (%)</label>
                          <input type="number" placeholder="No cambiar" value={bulkEditData.discount}
                            onChange={(e) => setBulkEditData(prev => ({...prev, discount: e.target.value}))} />
                        </div>
                        <div className="prodmgr-bulk-field">
                          <label>Estado</label>
                          <select value={bulkEditData.isActive}
                            onChange={(e) => setBulkEditData(prev => ({...prev, isActive: e.target.value}))}>
                            <option value="">No cambiar</option>
                            <option value="true">Activo</option>
                            <option value="false">Inactivo</option>
                          </select>
                        </div>
                        <div className="prodmgr-bulk-tags-field">
                          <label>Etiquetas para agregar</label>
                          <p>Se suman a las etiquetas que ya tiene cada producto.</p>
                          <div className="prodmgr-bulk-tag-groups">
                            {tagGroups.map(group => (
                              <div key={group.id}>
                                <span>{group.label}</span>
                                <div>
                                  {group.tags.map(tag => {
                                    const isSelected = bulkEditData.tagsToAdd.some(item => normalizeProductTag(item) === normalizeProductTag(tag));
                                    return <button key={tag} type="button" className={isSelected ? 'selected' : ''} onClick={() => toggleBulkTag(tag, 'add')} aria-pressed={isSelected}>{tag}</button>;
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="prodmgr-bulk-tags-field prodmgr-bulk-tags-field-remove">
                          <label>Etiquetas para quitar</label>
                          <p>Solo se eliminan de los productos seleccionados.</p>
                          <div className="prodmgr-bulk-tag-groups">
                            {tagGroups.map(group => (
                              <div key={group.id}>
                                <span>{group.label}</span>
                                <div>
                                  {group.tags.map(tag => {
                                    const isSelected = bulkEditData.tagsToRemove.some(item => normalizeProductTag(item) === normalizeProductTag(tag));
                                    return <button key={tag} type="button" className={isSelected ? 'selected' : ''} onClick={() => toggleBulkTag(tag, 'remove')} aria-pressed={isSelected}>{tag}</button>;
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="prodmgr-bulk-field prodmgr-bulk-apply">
                          <button className="prodmgr-bulk-apply-btn" onClick={handleBulkEdit} disabled={isApplyingBulkEdit}>
                            {isApplyingBulkEdit ? <><i className="fas fa-spinner fa-spin"></i> Aplicando...</> : <><i className="fas fa-check"></i> Aplicar</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Productos: Grid o Lista */}
            {isLoading ? (
              <Loader />
            ) : filteredProducts.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <i className="fas fa-box-open"></i>
                </div>
                <h3>No hay productos</h3>
                <p>Comienza agregando tu primer producto al catálogo</p>
              </div>
            ) : viewMode === 'list' ? (
              /* === VISTA LISTA === */
              <div className="product-list-view">
                <table className="product-list-table">
                  <thead>
                    <tr>
                      <th className="pl-col-check">
                        <input
                          type="checkbox"
                          checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="pl-col-img">Imagen</th>
                      <th className="pl-col-name">Producto</th>
                      <th className="pl-col-cat">Categoría</th>
                      <th className="pl-col-price">Precio</th>
                      <th className="pl-col-stock">Stock</th>
                      <th className="pl-col-status">Estado</th>
                      <th className="pl-col-variants">Variantes</th>
                      <th className="pl-col-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product) => {
                      const hasNewVariants = product.hasVariants && product.variants && product.variants.length > 0;
                      const hasLegacyVariants = (product.size && product.size.length > 0) || (product.color && product.color.length > 0);
                      const productStock = getProductStockValue(product);
                      const productImage = product.images && product.images.length > 0
                        ? product.images[0]
                        : (product.image && product.image !== 'https://via.placeholder.com/150' ? product.image : null);

                      return (
                        <tr key={product._id} className={product.isActive === false ? 'pl-row-inactive' : ''}>
                          <td className="pl-col-check">
                            <input
                              type="checkbox"
                              checked={selectedProducts.includes(product._id)}
                              onChange={() => handleSelectProduct(product._id)}
                            />
                          </td>
                          <td className="pl-col-img">
                            {productImage ? (
                              <img src={getDisplayImageUrl(productImage)} alt={product.name} className="pl-thumb" />
                            ) : (
                              <div className="pl-thumb-placeholder"><i className="fas fa-image"></i></div>
                            )}
                          </td>
                          <td className="pl-col-name">
                            <span className="pl-product-name">{product.name}</span>
                            {product.discount > 0 && (
                              <span className="pl-discount-tag">{product.discount}% OFF</span>
                            )}
                          </td>
                          <td className="pl-col-cat">
                            <span className="pl-cat-badge">{product.category?.name || product.category || '—'}</span>
                          </td>
                          <td className="pl-col-price">
                            <strong>${product.basePrice || product.price}</strong>
                          </td>
                          <td className="pl-col-stock">
                            <span className={`pl-stock-pill ${
                              productStock === null ? 'pl-stock-undef' : productStock > 0 ? 'pl-stock-ok' : 'pl-stock-out'
                            }`}>
                              {productStock === null ? 'N/D' : productStock}
                            </span>
                          </td>
                          <td className="pl-col-status">
                            <span className={`pl-status-dot ${product.isActive === false ? 'inactive' : 'active'}`}></span>
                            {product.isActive === false ? 'Inactivo' : 'Activo'}
                          </td>
                          <td className="pl-col-variants">
                            {hasNewVariants ? (
                              <span className="product-card-badge badge-success">{product.variants.length}</span>
                            ) : hasLegacyVariants ? (
                              <span className="product-card-badge badge-info">
                                {(product.size?.length || 0) + (product.color?.length || 0)}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="pl-col-actions">
                            <div className="pl-actions">
                              <button className="pl-action-btn pl-action-edit" onClick={() => handleEdit(product)} title="Editar">
                                <i className="fas fa-edit"></i>
                              </button>
                              <button className="pl-action-btn pl-action-duplicate" onClick={() => handleDuplicate(product)} title="Duplicar">
                                <i className="fas fa-copy"></i>
                              </button>
                              <button className="pl-action-btn pl-action-delete" onClick={() => handleDelete(product._id)} title="Eliminar">
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* === VISTA GRID (original) === */
              <div className="product-grid">
                {filteredProducts.map((product) => {
                  const hasNewVariants = product.hasVariants && product.variants && product.variants.length > 0;
                  const hasLegacyVariants = (product.size && product.size.length > 0) || (product.color && product.color.length > 0);
                  const productStock = getProductStockValue(product);
                  const stockInputValue = getStockInputValue(product);
                  const productImage = product.images && product.images.length > 0 
                    ? product.images[0] 
                    : (product.image && product.image !== 'https://via.placeholder.com/150' ? product.image : null);
                  
                  return (
                    <div key={product._id} className={`product-card ${product.isActive === false ? 'product-card-inactive' : ''}`}>
                      <div className="product-card-checkbox">
                        <input 
                          type="checkbox" 
                          checked={selectedProducts.includes(product._id)}
                          onChange={() => handleSelectProduct(product._id)}
                        />
                      </div>
                      
                      {productImage ? (
                        <img 
                          src={getDisplayImageUrl(productImage)} 
                          alt={product.name}
                          className="product-card-image"
                        />
                      ) : (
                        <div className="product-card-image-placeholder">
                          <i className="fas fa-image"></i>
                        </div>
                      )}
                      
                      <div className="product-card-body">
                        <h3 className="product-card-title">{product.name}</h3>
                        <p className="product-card-category">
                          {product.category?.name || product.category}
                          {product.isActive === false && (
                            <span className="product-card-status-dot inactive">Inactivo</span>
                          )}
                        </p>
                        
                        <div className="product-card-price-row">
                          <span className="product-card-price">${product.basePrice || product.price}</span>
                          {product.discount > 0 && (
                            <span className="product-card-discount">{product.discount}% OFF</span>
                          )}
                        </div>

                        <div className="product-card-stock">
                          <div className="product-card-stock-header">
                            <span className="product-card-stock-label">Stock</span>
                            <span
                              className={`product-card-stock-pill ${
                                productStock === null
                                  ? 'stock-pill-pending'
                                  : productStock > 0
                                    ? 'stock-pill-available'
                                    : 'stock-pill-empty'
                              }`}
                            >
                              {productStock === null ? 'Sin definir' : productStock > 0 ? productStock : 'Agotado'}
                            </span>
                          </div>
                          <div className="product-card-stock-editor">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className="product-card-stock-input"
                              value={stockInputValue}
                              onChange={(e) => handleStockDraftChange(product._id, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveStock(product._id);
                                }
                              }}
                              placeholder="0"
                            />
                            <button
                              type="button"
                              className="product-card-stock-button"
                              onClick={() => handleSaveStock(product._id)}
                              disabled={savingStockId === product._id}
                            >
                              {savingStockId === product._id ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                            </button>
                          </div>
                        </div>
                        
                        <div className="product-card-variants">
                          {hasNewVariants && (
                            <span className="product-card-badge badge-success">
                              {product.variants.length} variantes
                            </span>
                          )}
                          {hasLegacyVariants && !hasNewVariants && (
                            <>
                              {product.size && product.size.length > 0 && (
                                <span className="product-card-badge badge-info">
                                  {product.size.length} talles
                                </span>
                              )}
                              {product.color && product.color.length > 0 && (
                                <span className="product-card-badge badge-warning">
                                  {product.color.length} colores
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        
                        <div className="product-card-actions">
                          <button 
                            className="btn-card btn-card-edit"
                            onClick={() => handleEdit(product)}
                            title="Editar"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className="btn-card btn-card-duplicate"
                            onClick={() => handleDuplicate(product)}
                            title="Duplicar"
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                          <button 
                            className="btn-card btn-card-delete"
                            onClick={() => handleDelete(product._id)}
                            title="Eliminar"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Paginación */}
            {filteredProducts.length > 0 && pagination.totalPages > 1 && (
              <div className="pagination-container">
                <button 
                  className="btn-pagination"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  <i className="fas fa-chevron-left"></i> Anterior
                </button>
                
                <div className="pagination-info">
                  Página {currentPage} de {pagination.totalPages} ({pagination.total} productos)
                </div>
                
                <button 
                  className="btn-pagination"
                  onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                  disabled={currentPage === pagination.totalPages || isLoading}
                >
                  Siguiente <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Pestaña de Categorías */}
        {activeTab === 'categories' && (
          <CategoryManager />
        )}

        {activeTab === 'product-tags' && (
          <ProductTagManager />
        )}

        {/* Pestaña de Imágenes por Categoría */}
        {activeTab === 'category-images' && (
          <CategoryExtraImagesManager />
        )}
        
        {/* Pestaña de Carga Visual */}
        {activeTab === 'bulk-create' && (
          <BulkProductCreator />
        )}

        {/* Pestaña de Importación Masiva */}
        {activeTab === 'bulk-import' && (
          <BulkImport />
        )}
        
        {/* Pestaña de Órdenes */}
        {activeTab === 'orders' && (
          <Orders />
        )}
        
        {/* Pestaña de Emails */}
        {activeTab === 'emails' && (
          <EmailManager />
        )}

        {/* Pestaña de Biblioteca de Medios */}
        {activeTab === 'media' && (
          <EnhancedMediaLibrary mode="dashboard" />
        )}
        
        {/* Pestaña de Gestor de Variantes */}
        {activeTab === 'variants' && (
          <BulkVariantManager />
        )}
        
        {/* Pestaña de Descuento Masivo */}
        {activeTab === 'bulk-discount' && (
          <div>
            <div className="dashboard-header">
              <h1><i className="fas fa-percentage" style={{ marginRight: '10px' }}></i>Descuento Masivo</h1>
              <p>Aplica o elimina descuentos en lote por categoría</p>
            </div>
            <BulkDiscount />
          </div>
        )}
        
        {/* Pestaña de Chat Inteligente */}
        {activeTab === 'chat-stats' && (
          <div>
            <div className="dashboard-header">
              <h1><i className="fas fa-comments" style={{ marginRight: '10px' }}></i>Chat Inteligente</h1>
              <p>Monitorea el estado y funcionamiento del asistente IA</p>
            </div>
            <ChatStats />
          </div>
        )}

        {/* Pestaña de Pedidos Merchandising */}
        {activeTab === 'custom-orders' && (
          <div>
            <div className="dashboard-header">
              <h1>Pedidos Merchandising</h1>
              <p>Gestiona los pedidos de productos con estampado para empresas</p>
            </div>
            <CustomOrders />
          </div>
        )}

        {/* Pestaña de Gestor de Precios */}
        {activeTab === 'price-manager' && (
          <PriceManager />
        )}

        {/* Pestaña de Gestor de Stock */}
        {activeTab === 'stock-manager' && (
          <StockManager />
        )}

        {/* Pestaña de Gestor de Descripciones */}
        {activeTab === 'description-manager' && (
          <DescriptionManager />
        )}

        {/* Pestaña de Personalizado IA */}
        {activeTab === 'customizable-products' && (
          <CustomizableProductManager />
        )}

        {/* Pestaña de Configuraciones */}
        {activeTab === 'settings' && (
          <AdminSettings />
        )}
        </div> {/* Cierre dashboard-main */}
      </div> {/* Cierre dashboard-container */}
      
      {showMediaLibrary && (
        <div
          className="modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div style={{ backgroundColor: 'white', padding: '20px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h2>Subir Imagen</h2>
            <ImageUpload
              onUpload={(uploadedImage) => {
                setSelectedImage(uploadedImage);
              }}
            />
            <hr />
            <h2>Biblioteca de Medios</h2>
            <MediaLibrary
              onSelect={(image) => {
                setSelectedImage(image);
                setShowMediaLibrary(false);
              }}
            />
            <button
              type="button"
              className="btn btn-secondary mt-2"
              onClick={() => setShowMediaLibrary(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
